import React, { useEffect, useState } from "react";

import { Theme, useTheme } from "@mui/material/styles";

import { PackableStorageClientPageProps } from "./Packable.types";
import {
  Authorization,
  AuthorizedMenuItem,
  authorized,
  logDebug,
  mergeProperties,
} from "packable-components";
import { Grid, IconButton, List } from "@mui/material";

import HomeIcon from "@mui/icons-material/Home";
import ChairIcon from "@mui/icons-material/Chair";
import RoomIcon from "@mui/icons-material/Room";
import CategoryIcon from "@mui/icons-material/Category";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import PeopleIcon from "@mui/icons-material/People";
import TuneIcon from "@mui/icons-material/Tune";
import FolderSharedIcon from "@mui/icons-material/FolderShared";
import PersonIcon from "@mui/icons-material/Person";
import SettingsIcon from "@mui/icons-material/Settings";
import LogoutIcon from "@mui/icons-material/Logout";
import InfoIcon from "@mui/icons-material/Info";
import ContactSupportIcon from "@mui/icons-material/ContactSupport";
import InventoryIcon from "@mui/icons-material/Inventory";
import ViewStreamIcon from "@mui/icons-material/ViewStream";
import PaymentsIcon from "@mui/icons-material/Payments";
import { IUserAppState, PackablePage } from "packable-common";
import { PackableStorageClientContext } from "../context/PackableStorageClientContext.types";
import {
  ShippingCartStatus,
  ShippingCartType,
} from "../shippingcart/ShippingCart.types";
import { StorageShippingCartIcon } from "./StorageShippingCartIcon";
import { HomeShippingCartIcon } from "./HomeShippingCartIcon";
import { ShippingCartDialog } from "../shippingcart/ShippingCartDialog";

const useStyles = (theme: Theme) => ({
  PackablePageAppBarTransferToStorageCartButtonContainer: {
    pr: 2,
  },
  PackablePageAppBarTransferToStorageCartButton: {},
});

export const PackableStorageClientPage: React.FC<
  PackableStorageClientPageProps
> = (props): React.JSX.Element => {
  const { appLogoName, appLogo, appLogoSmall, content, styles } = props;

  const theme = useTheme();
  logDebug("PackableClientPage PROPS");
  logDebug(props);

  const defaultStyles = useStyles(theme);
  const effectiveStyles =
    styles === undefined
      ? defaultStyles
      : mergeProperties(defaultStyles, styles);

  logDebug("PackableClientPage Effective Styles:");
  logDebug(effectiveStyles);

  const { shippingCart } = React.useContext(PackableStorageClientContext);

  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [windowHeight, setWindowHeight] = useState(window.innerHeight);
  const [shippingCartShowing, setShippingCartShowing] =
    useState<boolean>(false);
  const [userAppState, setUserAppState] = useState<IUserAppState | null>(null);

  const isAdminUser = (): boolean => {
    return authorized("system", Authorization.MANAGE);
  };

  const handleOpenShippingCart = () => {
    setShippingCartShowing(true);
  };

  const handleCloseShippingCart = () => {
    setShippingCartShowing(false);
  };

  const onOrderCompleted = () => {
    setShippingCartShowing(false);
    window.location.href = "/items";
  };

  const updateWindowDimensions = () => {
    setWindowWidth(window.innerWidth);
    setWindowHeight(window.innerHeight);
  };

  useEffect(() => {
    updateWindowDimensions();
    window.addEventListener("resize", updateWindowDimensions);
  }, []);

  const renderNavigationMenu = (
    pathname: string,
    theme: Theme,
    effectiveStyles: any
  ) => {
    return (
      <>
        <List className="PackablePageDrawerMenu">
          <AuthorizedMenuItem
            sx={
              pathname === "/"
                ? effectiveStyles.PackablePageDrawerMenuItemActive
                : effectiveStyles.PackablePageDrawerMenuItem
            }
            className={
              pathname === "/"
                ? theme.palette.mode === "light"
                  ? "PackablePageDrawerMenuItemActiveLight"
                  : "PackablePageDrawerMenuItemActiveDark"
                : theme.palette.mode === "light"
                ? "PackablePageDrawerMenuItemLight"
                : "PackablePageDrawerMenuItemDark"
            }
            object="application"
            href="/"
            key="Home"
            primary="Home"
            icon={
              <HomeIcon
                className={
                  pathname === "/"
                    ? "PackablePageDrawerMenuItemIconActive"
                    : "PackablePageDrawerMenuItemIcon"
                }
                sx={
                  pathname === "/"
                    ? effectiveStyles.PackablePageDrawerMenuItemIconActive
                    : effectiveStyles.PackablePageDrawerMenuItemIcon
                }
              />
            }
          />
          <AuthorizedMenuItem
            sx={
              pathname.indexOf("/items") > -1
                ? effectiveStyles.PackablePageDrawerMenuItemActive
                : effectiveStyles.PackablePageDrawerMenuItem
            }
            className={
              pathname.indexOf("/items") > -1
                ? theme.palette.mode === "light"
                  ? "PackablePageDrawerMenuItemActiveLight"
                  : "PackablePageDrawerMenuItemActiveDark"
                : theme.palette.mode === "light"
                ? "PackablePageDrawerMenuItemLight"
                : "PackablePageDrawerMenuItemDark"
            }
            object="item"
            href="/items"
            key="Items"
            primary="Items"
            icon={
              <ChairIcon
                className={
                  pathname.indexOf("/items") > -1
                    ? "PackablePageDrawerMenuItemIconActive"
                    : "PackablePageDrawerMenuItemIcon"
                }
                sx={
                  pathname.indexOf("/items") > -1
                    ? effectiveStyles.PackablePageDrawerMenuItemIconActive
                    : effectiveStyles.PackablePageDrawerMenuItemIcon
                }
              />
            }
          />
          <AuthorizedMenuItem
            sx={
              pathname.includes("/spaces")
                ? effectiveStyles.PackablePageDrawerMenuItemActive
                : effectiveStyles.PackablePageDrawerMenuItem
            }
            className={
              pathname.includes("/spaces")
                ? theme.palette.mode === "light"
                  ? "PackablePageDrawerMenuItemActiveLight"
                  : "PackablePageDrawerMenuItemActiveDark"
                : theme.palette.mode === "light"
                ? "PackablePageDrawerMenuItemLight"
                : "PackablePageDrawerMenuItemDark"
            }
            object="space"
            href="/spaces"
            key="Spaces"
            primary="Spaces"
            icon={
              <RoomIcon
                className={
                  pathname.includes("/spaces")
                    ? "PackablePageDrawerMenuItemIconActive"
                    : "PackablePageDrawerMenuItemIcon"
                }
                sx={
                  pathname.includes("/spaces")
                    ? effectiveStyles.PackablePageDrawerMenuItemIconActive
                    : effectiveStyles.PackablePageDrawerMenuItemIcon
                }
              />
            }
          />
          <AuthorizedMenuItem
            sx={
              pathname.includes("/categories") ||
              pathname.includes("/subcategories")
                ? effectiveStyles.PackablePageDrawerMenuItemActive
                : effectiveStyles.PackablePageDrawerMenuItem
            }
            className={
              pathname.includes("/categories") ||
              pathname.includes("/subcategories")
                ? theme.palette.mode === "light"
                  ? "PackablePageDrawerMenuItemActiveLight"
                  : "PackablePageDrawerMenuItemActiveDark"
                : theme.palette.mode === "light"
                ? "PackablePageDrawerMenuItemLight"
                : "PackablePageDrawerMenuItemDark"
            }
            object="category"
            href="/categories"
            key="Categories"
            primary="Categories"
            icon={
              <CategoryIcon
                className={
                  pathname.includes("/categories") ||
                  pathname.includes("/subcategories")
                    ? "PackablePageDrawerMenuItemIconActive"
                    : "PackablePageDrawerMenuItemIcon"
                }
                sx={
                  pathname.includes("/categories") ||
                  pathname.includes("/subcategories")
                    ? effectiveStyles.PackablePageDrawerMenuItemIconActive
                    : effectiveStyles.PackablePageDrawerMenuItemIcon
                }
              />
            }
          />
          <AuthorizedMenuItem
            sx={
              pathname.includes("/tags")
                ? effectiveStyles.PackablePageDrawerMenuItemActive
                : effectiveStyles.PackablePageDrawerMenuItem
            }
            className={
              pathname.includes("/tags")
                ? theme.palette.mode === "light"
                  ? "PackablePageDrawerMenuItemActiveLight"
                  : "PackablePageDrawerMenuItemActiveDark"
                : theme.palette.mode === "light"
                ? "PackablePageDrawerMenuItemLight"
                : "PackablePageDrawerMenuItemDark"
            }
            object="item"
            href="/tags"
            key="Tags"
            primary="Tags"
            icon={
              <LocalOfferIcon
                className={
                  pathname.includes("/tags")
                    ? "PackablePageDrawerMenuItemIconActive"
                    : "PackablePageDrawerMenuItemIcon"
                }
                sx={
                  pathname.includes("/tags")
                    ? effectiveStyles.PackablePageDrawerMenuItemIconActive
                    : effectiveStyles.PackablePageDrawerMenuItemIcon
                }
              />
            }
          />
          {!isAdminUser() && (
            <AuthorizedMenuItem
              sx={
                pathname === "/inventories"
                  ? effectiveStyles.PackablePageDrawerMenuItemActive
                  : effectiveStyles.PackablePageDrawerMenuItem
              }
              className={
                pathname === "/inventories"
                  ? theme.palette.mode === "light"
                    ? "PackablePageDrawerMenuItemActiveLight"
                    : "PackablePageDrawerMenuItemActiveDark"
                  : theme.palette.mode === "light"
                  ? "PackablePageDrawerMenuItemLight"
                  : "PackablePageDrawerMenuItemDark"
              }
              object="inventory"
              href="/inventories"
              key="Inventories"
              primary="Inventories"
              icon={
                <InventoryIcon
                  className={
                    pathname === "/inventories"
                      ? "PackablePageDrawerMenuItemIconActive"
                      : "PackablePageDrawerMenuItemIcon"
                  }
                  sx={
                    pathname === "/inventories"
                      ? effectiveStyles.PackablePageDrawerMenuItemIconActive
                      : effectiveStyles.PackablePageDrawerMenuItemIcon
                  }
                />
              }
            />
          )}
          <AuthorizedMenuItem
            sx={
              pathname === "/orders"
                ? effectiveStyles.PackablePageDrawerMenuItemActive
                : effectiveStyles.PackablePageDrawerMenuItem
            }
            className={
              pathname === "/orders"
                ? theme.palette.mode === "light"
                  ? "PackablePageDrawerMenuItemActiveLight"
                  : "PackablePageDrawerMenuItemActiveDark"
                : theme.palette.mode === "light"
                ? "PackablePageDrawerMenuItemLight"
                : "PackablePageDrawerMenuItemDark"
            }
            object="order"
            href="/orders"
            key="Orders"
            primary="Orders"
            icon={
              <ViewStreamIcon
                className={
                  pathname === "/orders"
                    ? "PackablePageDrawerMenuItemIconActive"
                    : "PackablePageDrawerMenuItemIcon"
                }
                sx={
                  pathname === "/orders"
                    ? effectiveStyles.PackablePageDrawerMenuItemIconActive
                    : effectiveStyles.PackablePageDrawerMenuItemIcon
                }
              />
            }
          />
          {isAdminUser() && (
            <AuthorizedMenuItem
              sx={
                pathname === "/payments"
                  ? effectiveStyles.PackablePageDrawerMenuItemActive
                  : effectiveStyles.PackablePageDrawerMenuItem
              }
              className={
                pathname === "/payments"
                  ? theme.palette.mode === "light"
                    ? "PackablePageDrawerMenuItemActiveLight"
                    : "PackablePageDrawerMenuItemActiveDark"
                  : theme.palette.mode === "light"
                  ? "PackablePageDrawerMenuItemLight"
                  : "PackablePageDrawerMenuItemDark"
              }
              object="payment"
              href="/payments"
              key="Payments"
              primary="Payments"
              icon={
                <PaymentsIcon
                  className={
                    pathname === "/payments"
                      ? "PackablePageDrawerMenuItemIconActive"
                      : "PackablePageDrawerMenuItemIcon"
                  }
                  sx={
                    pathname === "/payments"
                      ? effectiveStyles.PackablePageDrawerMenuItemIconActive
                      : effectiveStyles.PackablePageDrawerMenuItemIcon
                  }
                />
              }
            />
          )}
          <AuthorizedMenuItem
            sx={
              pathname === "/users"
                ? effectiveStyles.PackablePageDrawerMenuItemActive
                : effectiveStyles.PackablePageDrawerMenuItem
            }
            className={
              pathname === "/users"
                ? theme.palette.mode === "light"
                  ? "PackablePageDrawerMenuItemActiveLight"
                  : "PackablePageDrawerMenuItemActiveDark"
                : theme.palette.mode === "light"
                ? "PackablePageDrawerMenuItemLight"
                : "PackablePageDrawerMenuItemDark"
            }
            object="packable_user"
            authorizedAction={Authorization.READ}
            href="/users"
            key="Users"
            primary="Users"
            icon={
              <PeopleIcon
                className={
                  pathname === "/users"
                    ? "PackablePageDrawerMenuItemIconActive"
                    : "PackablePageDrawerMenuItemIcon"
                }
                sx={
                  pathname === "/users"
                    ? effectiveStyles.PackablePageDrawerMenuItemIconActive
                    : effectiveStyles.PackablePageDrawerMenuItemIcon
                }
              />
            }
          />
          {isAdminUser() && (
            <AuthorizedMenuItem
              sx={
                pathname === "/clients"
                  ? effectiveStyles.PackablePageDrawerMenuItemActive
                  : effectiveStyles.PackablePageDrawerMenuItem
              }
              className={
                pathname === "/clients"
                  ? theme.palette.mode === "light"
                    ? "PackablePageDrawerMenuItemActiveLight"
                    : "PackablePageDrawerMenuItemActiveDark"
                  : theme.palette.mode === "light"
                  ? "PackablePageDrawerMenuItemLight"
                  : "PackablePageDrawerMenuItemDark"
              }
              object="entity"
              authorizedAction={Authorization.MANAGE}
              href="/clients"
              key="Clients"
              primary="Clients"
              icon={
                <FolderSharedIcon
                  className={
                    pathname === "/clients"
                      ? "PackablePageDrawerMenuItemIconActive"
                      : "PackablePageDrawerMenuItemIcon"
                  }
                  sx={
                    pathname === "/clients"
                      ? effectiveStyles.PackablePageDrawerMenuItemIconActive
                      : effectiveStyles.PackablePageDrawerMenuItemIcon
                  }
                />
              }
            />
          )}
          {isAdminUser() && (
            <AuthorizedMenuItem
              sx={
                pathname === "/subscription_plans"
                  ? effectiveStyles.PackablePageDrawerMenuItemActive
                  : effectiveStyles.PackablePageDrawerMenuItem
              }
              className={
                pathname === "/subscription_plans"
                  ? theme.palette.mode === "light"
                    ? "PackablePageDrawerMenuItemActiveLight"
                    : "PackablePageDrawerMenuItemActiveDark"
                  : theme.palette.mode === "light"
                  ? "PackablePageDrawerMenuItemLight"
                  : "PackablePageDrawerMenuItemDark"
              }
              object="subscription_plan"
              authorizedAction={Authorization.ADD}
              href="/subscription_plans"
              key="Plans"
              primary="Plans"
              icon={
                <TuneIcon
                  className={
                    pathname === "/subscription_plans"
                      ? "PackablePageDrawerMenuItemIconActive"
                      : "PackablePageDrawerMenuItemIcon"
                  }
                  sx={
                    pathname === "/subscription_plans"
                      ? effectiveStyles.PackablePageDrawerMenuItemIconActive
                      : effectiveStyles.PackablePageDrawerMenuItemIcon
                  }
                />
              }
            />
          )}
          {isAdminUser() && (
            <AuthorizedMenuItem
              sx={
                pathname === "/promotions"
                  ? effectiveStyles.PackablePageDrawerMenuItemActive
                  : effectiveStyles.PackablePageDrawerMenuItem
              }
              className={
                pathname === "/promotions"
                  ? theme.palette.mode === "light"
                    ? "PackablePageDrawerMenuItemActiveLight"
                    : "PackablePageDrawerMenuItemActiveDark"
                  : theme.palette.mode === "light"
                  ? "PackablePageDrawerMenuItemLight"
                  : "PackablePageDrawerMenuItemDark"
              }
              object="promotion"
              authorizedAction={Authorization.ADD}
              href="/promotions"
              key="Promotions"
              primary="Promotions"
              icon={
                <LocalOfferIcon
                  className={
                    pathname === "/promotions"
                      ? "PackablePageDrawerMenuItemIconActive"
                      : "PackablePageDrawerMenuItemIcon"
                  }
                  sx={
                    pathname === "/promotions"
                      ? effectiveStyles.PackablePageDrawerMenuItemIconActive
                      : effectiveStyles.PackablePageDrawerMenuItemIcon
                  }
                />
              }
            />
          )}
        </List>
      </>
    );
  };

  const renderAccountMenu = (
    pathname: string,
    theme: Theme,
    effectiveStyles: any
  ) => {
    return (
      <List
        className="PackablePageMenuList"
        sx={effectiveStyles.PackablePageMenuList}
        dense={true}
      >
        <AuthorizedMenuItem
          className="PackablePageUserMenuItem"
          sx={effectiveStyles.PackablePageUserMenuItem}
          object="application"
          href="/about"
          key="About"
          primary="About"
          disabled={pathname === "/about"}
          icon={
            <InfoIcon
              className={
                pathname === "/users"
                  ? "PackablePageUserMenuItemIconActive"
                  : "PackablePageUserMenuItemIcon"
              }
              sx={
                pathname === "/about"
                  ? effectiveStyles.PackablePageUserMenuItemIconActive
                  : effectiveStyles.PackablePageUserMenuItemIcon
              }
            />
          }
        />
        <AuthorizedMenuItem
          className="PackablePageUserMenuItem"
          sx={effectiveStyles.PackablePageUserMenuItem}
          object="packable_user"
          authorizedAction={Authorization.READ}
          href="/account"
          key="My Account"
          primary="My Account"
          disabled={pathname === "/account"}
          icon={
            <PersonIcon
              className={
                pathname === "/account"
                  ? "PackablePageUserMenuItemIconActive"
                  : "PackablePageUserMenuItemIcon"
              }
              sx={
                pathname === "/account"
                  ? effectiveStyles.PackablePageUserMenuItemIconActive
                  : effectiveStyles.PackablePageUserMenuItemIcon
              }
            />
          }
        />
        <AuthorizedMenuItem
          className="PackablePageUserMenuItem"
          sx={effectiveStyles.PackablePageUserMenuItem}
          object="subscription"
          href="/subscription"
          key="Subscription"
          primary="Subscription"
          disabled={pathname === "/subscription"}
          icon={
            <TuneIcon
              className={
                pathname === "/subscription"
                  ? "PackablePageUserMenuItemIconActive"
                  : "PackablePageUserMenuItemIcon"
              }
              sx={
                pathname === "/subscription"
                  ? effectiveStyles.PackablePageUserMenuItemIconActive
                  : effectiveStyles.PackablePageUserMenuItemIcon
              }
            />
          }
        />
        <AuthorizedMenuItem
          className="PackablePageUserMenuItem"
          sx={effectiveStyles.PackablePageUserMenuItem}
          object="application"
          href="/settings"
          key="Settings"
          primary="Settings"
          disabled={pathname === "/settings"}
          icon={
            <SettingsIcon
              className={
                pathname === "/settings"
                  ? "PackablePageUserMenuItemIconActive"
                  : "PackablePageUserMenuItemIcon"
              }
              sx={
                pathname === "/settings"
                  ? effectiveStyles.PackablePageUserMenuItemIconActive
                  : effectiveStyles.PackablePageUserMenuItemIcon
              }
            />
          }
        />
        <AuthorizedMenuItem
          className="PackablePageUserMenuItem"
          sx={effectiveStyles.PackablePageUserMenuItem}
          object="application"
          authorizedAction={Authorization.READ}
          href="/user/contact"
          key="Contact Us"
          primary="Contact Us"
          disabled={pathname === "/user/contact"}
          icon={
            <ContactSupportIcon
              className={
                pathname === "/user/contact"
                  ? "PackablePageUserMenuItemIconActive"
                  : "PackablePageUserMenuItemIcon"
              }
              sx={
                pathname === "/user/contact"
                  ? effectiveStyles.PackablePageUserMenuItemIconActive
                  : effectiveStyles.PackablePageUserMenuItemIcon
              }
            />
          }
        />
        <AuthorizedMenuItem
          className="PackablePageUserMenuItem"
          sx={effectiveStyles.PackablePageUserMenuItem}
          object="application"
          href="/logout"
          key="Logout"
          primary="Logout"
          disabled={pathname === "/logout"}
          icon={
            <LogoutIcon
              className={
                pathname === "/logout"
                  ? "PackablePageUserMenuItemIconActive"
                  : "PackablePageUserMenuItemIcon"
              }
              sx={
                pathname === "/logout"
                  ? effectiveStyles.PackablePageUserMenuItemIconActive
                  : effectiveStyles.PackablePageUserMenuItemIcon
              }
            />
          }
        />
      </List>
    );
  };

  const renderShippingCartIcon = (theme: Theme, effectiveStyles: any) => {
    if (
      shippingCart.cartStatus === ShippingCartStatus.ACTIVE &&
      shippingCart.cartType === ShippingCartType.STORAGE
    ) {
      return (
        <Grid sx={{ pr: 2 }}>
          <IconButton
            className="PackablePageAppBarTransferToStorageCartButton"
            sx={effectiveStyles.PackablePageAppBarTransferToStorageCartButton}
            edge="end"
            aria-label="outgoing transfer cart"
            aria-haspopup="true"
            onClick={handleOpenShippingCart}
            color="inherit"
          >
            <StorageShippingCartIcon
              itemCount={shippingCart.cartItems?.length}
            />
          </IconButton>
        </Grid>
      );
    }
    if (
      shippingCart.cartStatus === ShippingCartStatus.ACTIVE &&
      shippingCart.cartType === ShippingCartType.HOME
    ) {
      return (
        <Grid sx={{ pr: 2 }}>
          <IconButton
            className="PackablePageAppBarTransferToStorageCartButton"
            sx={effectiveStyles.PackablePageAppBarTransferToStorageCartButton}
            edge="end"
            aria-label="outgoing transfer cart"
            aria-haspopup="true"
            onClick={handleOpenShippingCart}
            color="inherit"
          >
            <HomeShippingCartIcon itemCount={shippingCart.cartItems?.length} />
          </IconButton>
        </Grid>
      );
    }
    return <></>;
  };

  return (
    <>
      <PackablePage
        appLogo={appLogo}
        appLogoName={appLogoName}
        appLogoSmall={appLogoSmall}
        renderNavigationMenu={renderNavigationMenu}
        renderAccountMenu={renderAccountMenu}
        renderAppBarActions={renderShippingCartIcon}
        onUserAppStateUpdate={setUserAppState}
        content={content}
        styles={effectiveStyles}
      />
      <ShippingCartDialog
        showShippingCart={shippingCartShowing}
        handleCloseShippingCart={handleCloseShippingCart}
        onOrderCompleted={onOrderCompleted}
        userAppState={userAppState}
        windowHeight={windowHeight}
        windowWidth={windowWidth}
        styles={effectiveStyles}
      />
    </>
  );
};

export default PackableStorageClientPage;
